import React, { useEffect, useState } from "react";
import { Button } from "antd";
import { CloseOutlined, DownloadOutlined, ShareAltOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { readStorage, writeStorage } from "../utils/safeStorage";

const DISMISSED_KEY = "saufdings-install-dismissed";
const wasDismissed = () => readStorage(DISMISSED_KEY) === "true";

type InstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

const isIosSafari = () => (/iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1))
    && /Safari/.test(navigator.userAgent)
    && !/(CriOS|FxiOS|EdgiOS|OPiOS)/.test(navigator.userAgent);

const InstallPrompt: React.FC = () => {
    const { t } = useTranslation();
    const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
    const [showIosHint, setShowIosHint] = useState(false);
    const [standalone, setStandalone] = useState(false);
    const [dismissed, setDismissed] = useState(wasDismissed);
    const [installError, setInstallError] = useState(false);

    useEffect(() => {
        const updateStandalone = () => setStandalone(isStandalone());
        const handleBeforeInstallPrompt = (event: Event) => {
            event.preventDefault();
            setInstallError(false);
            setPromptEvent(event as InstallPromptEvent);
        };
        const handleAppInstalled = () => {
            setPromptEvent(null);
            setStandalone(true);
        };
        const media = window.matchMedia?.("(display-mode: standalone)");

        updateStandalone();
        setShowIosHint(isIosSafari());
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);
        media?.addEventListener?.("change", updateStandalone);
        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
            media?.removeEventListener?.("change", updateStandalone);
        };
    }, []);

    const dismiss = () => {
        writeStorage(DISMISSED_KEY, "true");
        setDismissed(true);
    };

    const install = async () => {
        if (!promptEvent) return;
        try {
            await promptEvent.prompt();
            await promptEvent.userChoice;
        } catch {
            setInstallError(true);
        } finally {
            setPromptEvent(null);
        }
    };

    if (standalone || dismissed || (!promptEvent && !showIosHint && !installError)) return null;

    return (
        <aside className="sf-install-prompt" aria-label={t("installPrompt.title")}>
            <span>{promptEvent ? t("installPrompt.description") : t("installPrompt.iosInstructions")}</span>
            {installError && <span role="alert">{t("installPrompt.error")}</span>}
            {promptEvent && <Button type="primary" icon={<DownloadOutlined aria-hidden="true" />} onClick={() => void install()}>{t("installPrompt.install")}</Button>}
            {!promptEvent && showIosHint && <ShareAltOutlined aria-hidden="true" />}
            <Button type="text" className="sf-install-dismiss" aria-label={t("installPrompt.dismiss")} icon={<CloseOutlined />} onClick={dismiss} />
        </aside>
    );
};

export default InstallPrompt;
